import React, { useState } from 'react';
import { useImportContactsCSVMutation } from '../../store/services/whatsappApi';

interface InvalidContact {
  name: string;
  raw_phone: string;
  reason: string;
}

interface ContactsImportCorrectionProps {
  invalidContacts: InvalidContact[];
  onClose: () => void;
  onSuccess: () => void;
  groupId?: number;
}

const ContactsImportCorrection: React.FC<ContactsImportCorrectionProps> = ({
  invalidContacts,
  onClose,
  onSuccess,
  groupId
}) => {
  const [contacts, setContacts] = useState<Array<InvalidContact & { corrected_phone?: string }>>(() => 
    invalidContacts.map(contact => ({ ...contact, corrected_phone: formatPhoneNumber(contact.raw_phone) }))
  );
  const [importContactsCSV] = useImportContactsCSVMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Format phone number to international format
  function formatPhoneNumber(phone: string): string {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');
    
    // Add + prefix if not present
    return cleaned.startsWith('+') ? cleaned : `+${cleaned}`;
  }

  // Validate phone number format
  function isValidPhoneNumber(phone: string): boolean {
    // Valid format: +[country code][phone number], at least 7 digits after country code
    return /^\+\d{1,4}\d{7,}$/.test(phone);
  }

  // Handle phone number change
  const handlePhoneChange = (index: number, value: string) => {
    const newContacts = [...contacts];
    newContacts[index].corrected_phone = value;
    setContacts(newContacts);
  };

  // Handle submit corrected contacts
  const handleSubmit = async () => {
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      // Validate all phone numbers
      const invalidIndex = contacts.findIndex(
        contact => !contact.corrected_phone || !isValidPhoneNumber(contact.corrected_phone)
      );

      if (invalidIndex >= 0) {
        setError(`Invalid phone number format for ${contacts[invalidIndex].name}. Please use international format (e.g., +254712345678)`);
        setIsSubmitting(false);
        return;
      }

      // Create CSV content
      const headers = ['name', 'phone_number', 'email', 'notes'];
      const rows = contacts.map(contact => {
        return `${contact.name},${contact.corrected_phone},,`;
      });
      
      const csvContent = [headers.join(','), ...rows].join('\n');
      
      // Create a Blob and FormData
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'corrected_contacts.csv', { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('file', file);
      
      if (groupId) {
        formData.append('group_id', groupId.toString());
      }
      
      // Submit corrected contacts
      const result = await importContactsCSV(formData).unwrap();
      
      if (result.success) {
        setSuccess(`Successfully imported ${result.count} contacts`);
        setTimeout(() => {
          onSuccess();
        }, 1500);
      } else {
        setError('Failed to import contacts');
      }
    } catch (err: any) {
      console.error('Failed to import corrected contacts:', err);
      setError(err.data?.detail || 'Failed to import contacts');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-screen overflow-hidden">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-whatsapp-green text-white">
          <h3 className="text-lg font-semibold">Correct Contact Phone Numbers</h3>
          <button 
            onClick={onClose}
            className="text-white hover:text-gray-200"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="px-6 py-4">
          <p className="mb-4">
            We found {contacts.length} contacts with invalid phone numbers. Please correct them below.
            All phone numbers must be in international format (e.g., +254712345678).
          </p>

          {error && (
            <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {success && (
            <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700">{success}</p>
                </div>
              </div>
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Original Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Corrected Phone
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {contacts.map((contact, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{contact.raw_phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="text"
                        value={contact.corrected_phone || ''}
                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                        className={`border rounded px-2 py-1 text-sm ${
                          contact.corrected_phone && isValidPhoneNumber(contact.corrected_phone)
                            ? 'border-green-300'
                            : 'border-red-300'
                        }`}
                        placeholder="+254712345678"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.corrected_phone && isValidPhoneNumber(contact.corrected_phone) ? (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          Valid
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                          Invalid
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="px-6 py-3 bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 mr-2"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 bg-whatsapp-green text-white rounded-md hover:bg-whatsapp-dark-green"
          >
            {isSubmitting ? 'Importing...' : 'Import Corrected Contacts'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactsImportCorrection;
